/**
 * Toast wrapper with sound notifications
 * Plays a subtle sound when toasts appear
 */
import { toast as sonnerToast } from "sonner";

// Sound frequencies for different toast types
const SOUNDS = {
  success: { frequency: 800, duration: 150, type: 'sine' },
  error: { frequency: 300, duration: 200, type: 'square' },
  warning: { frequency: 500, duration: 180, type: 'triangle' },
  info: { frequency: 600, duration: 120, type: 'sine' },
  default: { frequency: 700, duration: 100, type: 'sine' }
};

// AudioContext singleton (created on first use)
let audioContext = null;

/**
 * Play a simple notification sound using Web Audio API
 */
function playSound(type = 'default') {
  try {
    // Create AudioContext on first use (must be after user interaction)
    if (!audioContext) {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }

    // Resume context if suspended (browser autoplay policy)
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }

    const sound = SOUNDS[type] || SOUNDS.default;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.type = sound.type;
    oscillator.frequency.setValueAtTime(sound.frequency, audioContext.currentTime);

    // Gentle volume envelope
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + 0.01);
    gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + sound.duration / 1000);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + sound.duration / 1000);
  } catch (e) {
    // Silently fail if audio is not available
    console.debug('Toast sound unavailable:', e.message);
  }
}

/**
 * Toast with sound - wraps sonner toast with sound notifications
 */
export const toast = {
  success: (message, options) => {
    playSound('success');
    return sonnerToast.success(message, options);
  },
  error: (message, options) => {
    playSound('error');
    return sonnerToast.error(message, options);
  },
  warning: (message, options) => {
    playSound('warning');
    return sonnerToast.warning(message, options);
  },
  info: (message, options) => {
    playSound('info');
    return sonnerToast.info(message, options);
  },
  // Default toast (no type specified)
  message: (message, options) => {
    playSound('default');
    return sonnerToast(message, options);
  },
  // Promise toast
  promise: (promise, options) => {
    return sonnerToast.promise(promise, {
      ...options,
      success: (data) => {
        playSound('success');
        return options?.success?.(data) || options?.success;
      },
      error: (error) => {
        playSound('error');
        return options?.error?.(error) || options?.error;
      }
    });
  },
  // Dismiss toast
  dismiss: sonnerToast.dismiss,
  // Loading toast (no sound)
  loading: sonnerToast.loading,
  // Custom toast
  custom: sonnerToast.custom,
};

export default toast;
